import { React, useEffect, useState } from "react";
import AppWrapper from "../../AppWrapper";
import supabase from "../../../supabase-client";
import { useNavigate } from "react-router-dom";
import {
  Page,
  Button,
  LegacyCard,
  IndexTable,
  Text,
  Pagination,
  TextField,
  BlockStack,
  Spinner,
  Badge,
  InlineStack,
} from "@shopify/polaris";
import { SearchIcon } from "@shopify/polaris-icons";
import { blockReasonLabel } from "../../../utility/clientBlocks";

function Customers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const pageSize = 20;

  const fetchCustomers = async () => {
    setLoading(true);
    const from = (page - 1) * pageSize;

    // search_customers matches every word of the term against the full
    // "nombre apellidos teléfono correo CURP RFC" string (accent-insensitive),
    // so "Adolfo Galvan" finds a customer whose name is split across columns.
    const { data, error } = await supabase.rpc("search_customers", {
      p_term: search.trim() || null,
      p_limit: pageSize,
      p_offset: from,
    });

    if (error) {
      console.error(error.message);
      setLoading(false);
      return;
    }

    setCustomers(data ?? []);
    setHasMore((data?.length ?? 0) >= pageSize);
    setLoading(false);
  };

  useEffect(() => {
    fetchCustomers();
  }, [page]);

  const rowMarkup = customers.map((customer, index) => (
    <IndexTable.Row
      id={customer.id}
      key={customer.id}
      position={index}
      onClick={() => navigate(`/customers/edit?id=${customer.id}`)}
    >
      <IndexTable.Cell>
        <InlineStack gap="200" blockAlign="center" wrap={false}>
          <Text fontWeight="semibold">
            {customer.first_name} {customer.last_name}
          </Text>
          {customer.blocked && (
            <Badge tone="critical">
              {`Bloqueado · ${blockReasonLabel(customer.block_reason)}`}
            </Badge>
          )}
        </InlineStack>
      </IndexTable.Cell>
      <IndexTable.Cell>{customer.phone || "-"}</IndexTable.Cell>
      <IndexTable.Cell>{customer.email || "-"}</IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <AppWrapper>
      <Page
        title="Clientes"
        primaryAction={
          <Button
            variant="primary"
            onClick={() => navigate("/customers/edit")}
          >
            Crear Cliente
          </Button>
        }
      >
        <BlockStack gap={400}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              fetchCustomers();
            }}
          >
            <div style={{ display: "flex", gap: "0.25rem" }}>
              <span style={{ flexGrow: 1 }}>
                <TextField
                  autoComplete="off"
                  placeholder="Buscar por nombre, teléfono, correo, CURP, RFC o #ID"
                  value={search}
                  onChange={(value) => setSearch(value)}
                  disabled={loading}
                />
              </span>
              <span
                style={{
                  aspectRatio: 1,
                  display: "flex",
                  alignItems: "center",
                  width: 32,
                  height: 32,
                }}
              >
                <Button icon={SearchIcon} submit disabled={loading} />
              </span>
            </div>
          </form>
          {loading ? (
            <Spinner size="small" />
          ) : (
            <LegacyCard>
              <>
                <IndexTable
                  resourceName={{
                    singular: "Cliente",
                    plural: "Clientes",
                  }}
                  selectable={false}
                  itemCount={customers?.length ?? 0}
                  headings={[
                    { title: "Nombre" },
                    { title: "Teléfono" },
                    { title: "Correo" },
                  ]}
                >
                  {rowMarkup}
                </IndexTable>
                <div
                  style={{
                    padding: "1rem",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Pagination
                    hasPrevious={page > 1}
                    onPrevious={() => setPage(page - 1)}
                    hasNext={hasMore}
                    onNext={() => setPage(page + 1)}
                  />
                </div>
              </>
            </LegacyCard>
          )}
        </BlockStack>
      </Page>
    </AppWrapper>
  );
}

export default Customers;
